import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useNavigationPage from '../../hooks/useNavigationPage';
import useBatchTypeService from '../../app/services/hooks/useBatchTypeService';
import { BatchType, BatchTypeFilters } from '../../app/models/BatchType';
import BatchTypeDataTable from './components/tables/BatchTypeDataTable';
import BatchTypeCreate from './components/BatchTypeCreate';
import BatchTypeEdit from './components/BatchTypeEdit';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Tipos de lotes',
        url: '/batch-types',
        isActive: true
    }
];

const BatchTypes = () => {
    const { navigationPage } = useNavigationPage();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetBatchTypes, getBatchTypes, deleteBatchType, activeBatchType } =
        useBatchTypeService();
    const [batchTypes, setBatchTypes] = useState<BatchType[]>([]);

    const [batchTypeIdEdit, setBatchTypeIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [batchTypesFilter, setBatchTypesFilter] = useState<BatchTypeFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [batchTypesFilter]);

    const getAllBatchTypes = () => {
        getBatchTypes(batchTypesFilter, {
            onSuccess: (response: ServiceResponse) => {
                setBatchTypes(response.data.batch_types);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (batchTypesId: number) => {
        setShowingEdit(true);
        setBatchTypeIdEdit(batchTypesId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setBatchTypeIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllBatchTypes();
    };

    const destroy = (batchTypesId: number) => {
        const _text = 'Está a punto de eliminar el tipo de lote #' + batchTypesId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando tipo de lote...');
                        deleteBatchType(batchTypesId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El tipo de lote no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Tipos de lotes" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo tipo de lote" />}
                />
                <BatchTypeDataTable
                    batchTypes={batchTypes}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetBatchTypes}
                    filter={batchTypesFilter}
                    setFilter={setBatchTypesFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear tipo de lote"
                    backdrop={true}
                    showFooter={false}
                >
                    <BatchTypeCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar tipo de lote"
                    backdrop={true}
                    showFooter={false}
                >
                    <BatchTypeEdit
                        batchTypeId={batchTypeIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default BatchTypes;
